import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { Guid } from "guid-string";
import { AsyncLoadResult, AsyncLoadOptions } from '../../../../shared/abstractStore';
import { useQuestionnaireSummaryViewModelQuery, useQuestionnaireSummaryViewModelQueryVariables, } from '../../generated/useQuestionnaireSummaryViewModelQuery';
import { questionnaireFieldsFragment } from '../../models/Questionnaire';
import { mainApiConfig } from '../../../../configure/mainApiConfig';
import { questionnaireSectionFieldsFragment } from '../../models/QuestionnaireSection';
import { questionFieldsFragment } from '../../models/Question';
import { learningUnitFieldsFragment } from '../../models/LearningUnit';
import { questionnaireRespondentSessionFieldsFragment } from '../../models/QuestionnaireRespondentSession';

/**
 * Get a Questionnaire with sections and questions from the store along with related sessions for all users.
 * @param id
 * @param options
 */
export function useQuestionnaireSummaryViewModel(id: string | undefined | null, options: AsyncLoadOptions = {}): AsyncLoadResult<useQuestionnaireSummaryViewModelQuery> {
    // Query the data we need form the api.
    const [load, { data, loading, error, refetch }] = useLazyQuery<useQuestionnaireSummaryViewModelQuery, useQuestionnaireSummaryViewModelQueryVariables>(
        gql`
        query useQuestionnaireSummaryViewModelQuery ($id: ID!) {
            model: questionnaire(id: $id) {
                ...questionnaireFields

                questionnaireSections {
                    ...questionnaireSectionFields
                }

                questions {
                    ...questionFields
                }

            }

            learningUnit: learningUnit (questionnaireId: $id) {
                ...learningUnitFields
            }

            questionnaireRespondentSessions: questionnaireRespondentSessions(questionnaireId: $id){
                ...questionnaireRespondentSessionFields
            }
            
        }

        ${questionnaireFieldsFragment}
        ${questionnaireSectionFieldsFragment}
        ${questionFieldsFragment}
        ${learningUnitFieldsFragment}
        ${questionnaireRespondentSessionFieldsFragment}
        `,
        {
            variables: {
                id: id ?? Guid.empty,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}
